import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Grid2 as Grid,
  Typography,
  ButtonGroup,
  Button,
  IconButton,
  Card,
  Alert,
  AlertTitle,
  useTheme
} from '@mui/material';
import {
  Approval as ApprovalIcon,
  DoNotDisturb as DoNotDisturbIcon,
  Plagiarism as PlagiarismIcon,
  Construction as ConstructionIcon
} from '@mui/icons-material';

import { getIsIncomplete } from '../utilities.js';
import { EncodingDisplayInfo } from './encodingDisplayInfo.jsx';

const INCOMPLETE_STATUS_VALUE = 0;
const ACCEPTED_STATUS_VALUE = 1;
const REJECTED_STATUS_VALUE = 2;

const LINE_ITEM_STATUSES = {
  INCOMPLETE: INCOMPLETE_STATUS_VALUE,
  ACCEPTED: ACCEPTED_STATUS_VALUE,
  REJECTED: REJECTED_STATUS_VALUE
};

const getAvatarDisplayAndTitleByStatus = (isRejected, isAccepted) => {
  const isIncomplete = getIsIncomplete(isRejected, isAccepted);
  const display = isIncomplete === true
    ? 'I'
    : isAccepted === true
      ? 'A'
      : 'R';

  const title = isIncomplete === true
    ? 'Incomplete'
    : isAccepted === true
      ? 'Accepted'
      : 'Rejected';

  const status = isIncomplete === true
    ? LINE_ITEM_STATUSES.INCOMPLETE
    : isAccepted === true
      ? LINE_ITEM_STATUSES.ACCEPTED
      : LINE_ITEM_STATUSES.REJECTED;

  return { display, title, status };
};

function EncounterLineItemActionButtons({
  isAccepted,
  isRejected,
  openAcceptRejectDialog,
  lineItem
}) {
  return (
    <ButtonGroup variant="outlined" disableElevation fullWidth>
      <Button
        color="success"
        startIcon={<ApprovalIcon />}
        disabled={isAccepted}
        onClick={() => openAcceptRejectDialog(lineItem, 'accept')}
      >
        Accept
      </Button>
      <Button
        color="error"
        startIcon={<DoNotDisturbIcon />}
        disabled={isRejected}
        onClick={() => openAcceptRejectDialog(lineItem, 'reject')}
      >
        Reject
      </Button>
    </ButtonGroup>
  );
}

const createLineItemSx = (theme, lineItemStatus) => {
  const getBackgroundColor = () => {
    return lineItemStatus === LINE_ITEM_STATUSES.INCOMPLETE
      ? { backgroundColor: theme.palette.grey[200] }
      : null
  };

  return {
    ...getBackgroundColor()
  };
};

function EncounterLineItem({
  encounterLineItemId,
  encounterId,
  encodingDisplayInfo,
  isAccepted,
  isRejected,
  openAcceptRejectDialog,
  reasonOptionIdList,
  // completedOn = '',
  // completedBy = '',
}) {
  const theme = useTheme();
  const { dx } = encodingDisplayInfo;

  const avatarDisplay = getAvatarDisplayAndTitleByStatus(isRejected, isAccepted);

  const lineItem = {
    encounterLineItemId,
    encounterId,
    reasonOptionIdList,
    isAccepted,
    isRejected
  };

  return (
    <Card sx={{ marginBottom: '8px' }}>
      <Grid container spacing={1}>
        {/* encoding display info */}
        <Grid alignSelf="flex-start" size={12}>
          <Alert
            color={
              avatarDisplay.display === 'A'
                ? 'success'
                : avatarDisplay.display === 'R'
                  ? 'error'
                  : 'warning'
            }
            severity={
              avatarDisplay.display === 'A'
                ? 'success'
                : avatarDisplay.display === 'R'
                  ? 'error'
                  : 'warning'
            }
            icon={
              avatarDisplay.display === 'A'
                ? <ApprovalIcon />
                : avatarDisplay.display === 'R'
                  ? <DoNotDisturbIcon />
                  : <ConstructionIcon
                      sx={{
                        color: theme.palette.getContrastText(
                          theme.palette.grey[200]
                        )
                      }}
                    />
            }
            sx={createLineItemSx(theme, avatarDisplay.status)}
          >
            <AlertTitle>
              STATUS {avatarDisplay.title}

              {/* conditional render for rejection reason dialog */}
              {/* {
                isRejected === true
                  ? (
                    <Button
                      disableElevation
                      color="error"
                      variant="outlined"
                      startIcon={<PlagiarismIcon />}
                      disabled={isAccepted}
                      onClick={() => openAcceptRejectDialog(lineItem)}
                      sx={{ display: 'inline-block', marginLeft: '24px' }}
                    >
                      Show Rejection Reason
                    </Button>
                  )
                  : null
              } */}
            </AlertTitle>
            <EncounterLineItemActionButtons
                isAccepted={isAccepted}
                isRejected={isRejected}
                openAcceptRejectDialog={openAcceptRejectDialog}
                lineItem={lineItem}
                encounterId={encounterId}
                encounterLineItemId={encounterLineItemId}
              />

            <EncodingDisplayInfo dx={dx} theme={theme} />
          </Alert>
        </Grid>
      </Grid>
    </Card>
  );
}

EncounterLineItem.propTypes = {
  encounterId: PropTypes.number.isRequired,
  encounterLineItemId: PropTypes.number.isRequired,
  // completedBy: PropTypes.string,
  // completedOn: PropTypes.string,
  // createdOn: PropTypes.string.isRequired,
  encodingDisplayInfo: PropTypes.shape({
    dx: PropTypes.shape({
      encodingId: PropTypes.number.isRequired,
      code: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    }).isRequired,
  }).isRequired,
  isAccepted: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool.isRequired,
  reasonOptionIdList: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  openAcceptRejectDialog: PropTypes.func.isRequired
};

export { EncounterLineItem };
